<script setup lang="ts">
import { useVuelidate } from "@vuelidate/core";
import { required, email } from "@vuelidate/validators";
import type { CmsElementForm } from "@shopware/composables";
import { useCmsTranslations } from "@shopware/composables";
import { ApiClientError, type ApiError } from "@shopware/api-client";
import { useCmsElementConfig, useNewsletter, useSalutations } from "#imports";
import {computed, onMounted, reactive, ref} from "vue";
import { defu } from "defu";
import type { CmsElementNewsletter } from "~/composables/useCustom";

const props = defineProps<{
  content: CmsElementNewsletter;
  class: any;
  style: any;
}>();

const { t } = useI18n()
const { pushSuccess } = useNotifications();
const { heading, subHeading, text } = props.content?.config

type Translations = {
  form: {
    subscribeLabel: string;
    unsubscribeLabel: string;
    action: string;
    email: string;
    emailPlaceholder: string;
    salutation: string;
    salutationPlaceholder: string;
    firstName: string;
    firstNamePlaceholder: string;
    lastName: string;
    lastNamePlaceholder: string;
    privacy: string;
    privacyLabel: string;
    submit: string;
    newsletterBenefits: string;
  };
};

let translations: Translations = {
  form: {
    subscribeLabel: "Subscribe to newsletter",
    unsubscribeLabel: "Unsubscribe from newsletter",
    action: "Action",
    email: "Email address",
    emailPlaceholder: "Enter email address...",
    salutation: "Salutation",
    salutationPlaceholder: "Enter salutation...",
    firstName: "First name",
    firstNamePlaceholder: "Enter first name...",
    lastName: "Last name",
    lastNamePlaceholder: "Enter last name...",
    privacy: "Privacy",
    privacyLabel: "I have read the data protection information.",
    submit: "Submit",
    newsletterBenefits:
        "Be aware of upcoming sales and events.Receive gifts and special offers!",
  },
};

const {
  isLoggedIn,
  user,
} = useUser();

translations = defu(useCmsTranslations(), translations) as Translations;

const loading = ref<boolean>();
const formSent = ref<boolean>(false);
const errorMessages = ref<ApiError[]>([]);
const subscriptionOptions: {
  label: string;
  value: "subscribe" | "unsubscribe";
}[] = [
  {
    label: translations.form.subscribeLabel,
    value: "subscribe",
  },
  {
    label: translations.form.unsubscribeLabel,
    value: "unsubscribe",
  },
];
const { getSalutations } = useSalutations();
const { getConfigValue } = useCmsElementConfig(props.content);
const { newsletterSubscribe, newsletterUnsubscribe } = useNewsletter();

const getFormTitle = computed(() => getConfigValue("title"));
const state = reactive({
  option: subscriptionOptions[0].value,
  salutationId: "",
  firstName: "Guest",
  lastName: "User",
  email: "",
  checkbox: true,
});

const rules = computed(() => {
  let temp: any = {
    email: {
      required,
      email,
    },
    checkbox: {
      required,
      isTrue: (value: any) => value === true,
    },
  };
  if (state.option === "subscribe") {
    temp = {
      ...temp,
      firstName: {
        required: false,
        minLength: 3,
      },
      lastName: {
        required: false,
        minLength: 3,
      },
      salutationId: {
        required: false,
      },
    };
  }
  return temp;
});

const $v = useVuelidate(rules, state);
const invokeSubmit = async () => {
  $v.value.$touch();
  const valid = await $v.value.$validate();
  if (valid) {
    loading.value = true;
    try {
      if (state.option === "subscribe") {
        await newsletterSubscribe({
          ...state,
        });
        pushSuccess(t('signup.newsletter.success'))
      } else {
        await newsletterUnsubscribe(state.email);
        pushSuccess(t('unsubscribe.newsletter.success'))
      }
      formSent.value = true;
    } catch (e) {
      if (e instanceof ApiClientError) {
        errorMessages.value = e.details.errors;
      }
    } finally {
      loading.value = false;
    }
  }
};

onMounted(()=> {
  if(isLoggedIn.value) {
    state.firstName = user.value?.firstName || "";
    state.lastName = user.value?.lastName || "";
    state.email = user.value?.email || "";
    state.salutationId = user.value?.salutationId || "";
  } else {
    state.firstName = "Guest";
    state.lastName = "User";
    state.salutationId = "018d5a8cac6f703790ae3b42b0fd58ae";
  }
})
</script>
<template>
  <section class="section section-main section-subscribe section--solid"  :style="props.style" :class="props.class">
    <div class="container">
      <div class="section__inner">
        <header class="section__head">
          <h2>{{ heading.value }}</h2>

          <p>{{ subHeading.value }}</p>
        </header><!-- /.section__head -->

        <div class="section__body">
          <div class="subscribe section__subscribe">
            <form class="w-full relative" @submit.prevent="invokeSubmit">
              <div class="subscribe__inner">
                <div class="col-span-12 d-none">
                  <label for="option">{{ translations.form.action }} *</label>
                  <select id="option" name="option" v-model="state.option">
                    <option v-for="subscription in subscriptionOptions" :key="subscription.value" :value="subscription.value">
                      {{ subscription.label }}
                    </option>
                  </select>
                </div>

                <label class="hidden form-label" for="email-address">{{$t('newsletter.email.label')}} *</label>
                <input
                    id="email-address"
                    name="email"
                    type="email"
                    autocomplete="email"
                    :class="[
                            $v.email.$error
                            ? 'border-danger'
                            : 'border-gray-300 focus:border-indigo-500',
                            ]"
                    v-model="state.email"
                    @blur="$v.email.$touch()"
                    class="subscribe__field form-control"
                    :placeholder="t('newsletter.form.emailPlaceholder')"
                />

                <div v-if="state.option === 'subscribe'" class="col-span-4 d-none">
                  <label for="salutation">{{ translations.form.salutation }} *</label>
                  <select id="salutation" name="salutation"
                          v-model="state.salutationId"
                          @blur="$v.salutationId.$touch()">
                    <option disabled selected value="">
                      {{ translations.form.salutationPlaceholder }}
                    </option>
                    <option
                        v-for="salutation in getSalutations"
                        :key="salutation.id"
                        :value="salutation.id"
                    >
                      {{ salutation.displayName }}
                    </option>
                  </select>
                  <span
                      v-if="$v.salutationId.$error"
                      class="pt-1 text-sm text-red-600 focus:ring-brand-primary border-gray-300"
                  >
                    {{ $v.salutationId.$errors[0].$message }}
                  </span>
                </div>
                <div v-if="state.option === 'subscribe'" class="col-span-4 d-none">
                  <label for="first-name">{{ translations.form.firstName }} *</label>
                  <input
                      id="first-name"
                      name="first-name"
                      type="text"
                      autocomplete="first-name"
                      @blur="$v.firstName.$touch()"
                      v-model="state.firstName"
                      :placeholder="translations.form.firstNamePlaceholder"
                  />
                  <span
                      v-if="$v.firstName.$error"
                      class="pt-1 text-sm text-red-600 focus:ring-brand-primary border-gray-300"
                  >
            {{ $v.firstName.$errors[0].$message }}
          </span>
                </div>
                <div v-if="state.option === 'subscribe'" class="col-span-4 d-none">
                  <label for="last-name">{{ translations.form.lastName }} *</label>
                  <input
                      id="last-name"
                      name="last-name"
                      type="text"
                      autocomplete="last-name"
                      v-model="state.lastName"
                      @blur="$v.lastName.$touch()"
                      :placeholder="translations.form.lastNamePlaceholder"
                  />
                  <span
                      v-if="$v.lastName.$error"
                      class="pt-1 text-sm text-red-600 focus:ring-brand-primary border-gray-300"
                  >
            {{ $v.lastName.$errors[0].$message }}
          </span>
                </div>
                <button class="subscribe__btn btn btn-default btn-danger" type="submit">{{$t('newsletter.email.submit')}}</button>
              </div>
              <p v-if="$v.email.$error" class="text-danger mt-2 text-start">{{ $v.email.$errors[0].$message }}</p>
              <!--label class="hidden form-label">{{ t('newsletter.form.privacy') }} *</label>
              <div class="d-flex my-3">
                <input
                  id="privacy"
                  name="privacy"
                  type="checkbox"
                  v-model="state.checkbox"
                  class="me-3"
                  :class="[$v.checkbox.$error ? 'border-red-600' : 'border-gray-300',]"
                />
                <div>
                  <label :class="[$v.checkbox.$error ? 'text-red-600' : '']" for="privacy">
                    {{  t('newsletter.form.privacyLabel') }}
                  </label>
                </div>
              </div-->
              <template v-if="!formSent">

              </template>
              <template v-else>
                <p class="py-10 text-lg text-center">

                </p>
              </template>
            </form>
          </div><!-- /.subscribe -->

          <p>{{ text.value }}</p>
        </div><!-- /.section__body -->
      </div>
    </div><!-- /.container -->
  </section>
</template>